/** Pricing display helpers — amounts mirror backend/utils/billing_catalog.py */

export const TRIAL_DAYS = 7;

export const CURRENCIES = ['INR', 'USD'];

export const PAID_SLUGS = [];

export const FEATURE_MATRIX = [
  { key: 'secure_qr_generation', label: 'Secure QR generation' },
  { key: 'consumer_verify', label: 'Consumer verify portal' },
  { key: 'gs1_digital_link', label: 'GS1 Digital Link' },
  { key: 'workflows', label: 'Workflows' },
  { key: 'region_enforcement', label: 'Region enforcement' },
  { key: 'counterfeit_alerts', label: 'Counterfeit alerts' },
  { key: 'api_access', label: 'API access' },
  { key: 'white_label_verify', label: 'Custom verify domain' },
  { key: 'on_prem', label: 'On-prem deployment' },
];

const TIER_ORDER = ['trial', 'enterprise'];

const FALLBACK_PLANS = {
  INR: {
    trial: { monthly: 0, yearly: 0 },
    enterprise: { monthly: null, yearly: null },
  },
  USD: {
    trial: { monthly: 0, yearly: 0 },
    enterprise: { monthly: null, yearly: null },
  },
};

const MARKETING_FEATURES = {
  trial: [
    `${TRIAL_DAYS}-day full platform trial`,
    'Consumer verify portal',
    'Basic analytics',
    'Email support (48h)',
  ],
  enterprise: [
    'Unlimited volume (custom)',
    'On-premise deployment',
    'Custom SLA & integrations',
    'Dedicated account manager',
  ],
};

const FALLBACK_TIER = {
  trial: {
    limits: { products: 10, qrCodes: 50, scans: 500, adminSeats: 2, mobileSeats: 3, workflows: 1 },
    featurePackaging: {
      secure_qr_generation: true,
      consumer_verify: true,
      gs1_digital_link: false,
      workflows: 'basic',
      region_enforcement: false,
      counterfeit_alerts: 'basic',
      api_access: 'none',
      white_label_verify: false,
      on_prem: false,
    },
  },
  enterprise: {
    limits: { products: null, qrCodes: null, scans: null, adminSeats: null, mobileSeats: null, workflows: null },
    featurePackaging: {
      secure_qr_generation: true,
      consumer_verify: true,
      gs1_digital_link: true,
      workflows: 'unlimited',
      region_enforcement: true,
      counterfeit_alerts: 'full',
      api_access: 'full',
      white_label_verify: true,
      on_prem: true,
    },
  },
};

export function formatPlanPrice(amount, currency = 'INR') {
  if (amount == null) return 'Custom';
  if (amount === 0) return currency === 'USD' ? '$0' : '₹0';
  if (currency === 'USD') {
    return `$${Number(amount).toLocaleString('en-US')}`;
  }
  return `₹${Number(amount).toLocaleString('en-IN')}`;
}

export function yearlySavingsLabel() {
  return '';
}

export function planPrice(slug, cycle, currency = 'INR') {
  const cur = FALLBACK_PLANS[currency] || FALLBACK_PLANS.INR;
  const tier = cur[slug];
  if (!tier) return null;
  if (cycle === 'yearly') return tier.yearly;
  return tier.monthly;
}

export function displayPlansForCurrency(currency = 'INR', apiPlans = []) {
  const cur = (currency || 'INR').toUpperCase();
  const bySlug = {};
  (apiPlans || []).forEach((p) => {
    if ((p.currency || '').toUpperCase() !== cur) return;
    const slug = p.slug || 'trial';
    if (!TIER_ORDER.includes(slug)) return;
    if (!bySlug[slug]) bySlug[slug] = {};
    bySlug[slug][p.billing_cycle] = p;
  });

  return TIER_ORDER.map((slug) => {
    const monthly = bySlug[slug]?.monthly;
    const yearly = bySlug[slug]?.yearly;
    const trial = bySlug[slug]?.trial;
    const custom = bySlug[slug]?.custom;
    const ref = monthly || yearly || trial || custom || {};
    const fallback = FALLBACK_PLANS[cur]?.[slug] || {};
    const tierFallback = FALLBACK_TIER[slug] || {};
    const hasApiLimits = ref.product_limit != null || ref.qr_code_limit != null;
    const apiPackaging = ref.feature_packaging || ref.feature_flags;
    return {
      slug,
      name: ref.name || slug.charAt(0).toUpperCase() + slug.slice(1),
      currency: cur,
      monthlyPrice: monthly?.price ?? fallback.monthly,
      yearlyPrice: yearly?.price ?? fallback.yearly,
      features: (ref.features && ref.features.length)
        ? ref.features
        : (MARKETING_FEATURES[slug] || []),
      featurePackaging: apiPackaging && Object.keys(apiPackaging).length
        ? apiPackaging
        : tierFallback.featurePackaging || {},
      limits: hasApiLimits
        ? {
            products: ref.product_limit,
            qrCodes: ref.qr_code_limit,
            scans: ref.scan_limit,
            adminSeats: ref.admin_seat_limit,
            mobileSeats: ref.mobile_seat_limit,
            workflows: ref.workflow_limit,
          }
        : tierFallback.limits || {},
      isCustom: slug === 'enterprise',
      isTrial: slug === 'trial',
    };
  });
}

export function featureCellValue(packaging, key) {
  const ff = packaging || {};
  const v = ff[key];
  if (v === true) return '✓';
  if (v === false || v === 'none' || !v) return '—';
  if (typeof v === 'string') {
    const labels = {
      basic: 'Basic',
      full: 'Full',
      readonly: 'Read-only',
      unlimited: 'Unlimited',
      custom: 'Custom',
    };
    return labels[v] || v;
  }
  return '✓';
}

export function readCurrencyFromSearch(search) {
  const q = new URLSearchParams(search || '');
  const c = (q.get('currency') || '').toUpperCase();
  return CURRENCIES.includes(c) ? c : 'INR';
}

export function normalizePlanSelection(plan, cycle) {
  const p = (plan || 'free').toLowerCase();
  const c = (cycle || 'trial').toLowerCase();
  if (p === 'enterprise') return { plan: 'enterprise', cycle: 'custom' };
  return { plan: 'free', cycle: 'trial' };
}

export function formatSelectedPlanLabel(plan, cycle, currency = 'INR') {
  const p = (plan || 'free').toLowerCase();
  const cur = (currency || 'INR').toUpperCase();
  if (p === 'enterprise') return 'Enterprise (custom)';
  return `Free trial · ${TRIAL_DAYS} days · ${cur}`;
}
